import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { inr, imageUrl } from "@/lib/api";
import { WISHLIST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function WishlistPage() {
  const { products, toggle, loaded } = useWishlist();
  const { addItem } = useCart();
  const navigate = useNavigate();

  const addAndRemove = async (p) => {
    addItem({
      kind: "product",
      ref_id: p.id,
      name: p.name,
      unit_price: p.price,
      mrp: p.mrp,
      image_url: imageUrl(p.images?.[0] || ""),
      category_id: p.category_id,
      is_green_cracker: p.is_green_cracker,
    });
    await toggle(p.id);
    toast.success(`${p.name} moved to cart`);
  };

  if (!loaded) return <div className="py-12 text-center text-hyd-text2">Loading...</div>;

  if (products.length === 0) {
    return (
      <div data-testid={WISHLIST.empty} className="max-w-md mx-auto px-4 py-16 text-center">
        <Heart className="w-10 h-10 mx-auto text-hyd-text3" />
        <h2 className="font-display text-2xl font-bold mt-3">Your wishlist is empty</h2>
        <p className="text-hyd-text2 text-sm mt-1">Tap the heart on any product to save it here.</p>
        <Button onClick={() => navigate("/")} className="mt-4 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim">Browse crackers</Button>
      </div>
    );
  }

  return (
    <div data-testid={WISHLIST.page} className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="font-display text-3xl font-extrabold">Wishlist</h1>
      <p className="text-hyd-text2 text-sm mt-1">{products.length} saved item{products.length > 1 ? "s" : ""}</p>

      <div className="mt-5 space-y-3">
        {products.map((p) => (
          <div key={p.id} data-testid={WISHLIST.row(p.id)} className="bg-hyd-panel border border-hyd-border rounded-xl p-3 flex gap-3">
            <Link to={`/`} className="w-20 h-20 rounded-lg overflow-hidden bg-hyd-panel2 shrink-0">
              {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
            </Link>
            <div className="flex-1">
              <div className="font-semibold line-clamp-1">{p.name}</div>
              <div className="text-hyd-gold font-bold text-sm mt-0.5">{inr(p.price)}
                {p.mrp > p.price && <span className="text-hyd-text3 line-through text-xs ml-2">{inr(p.mrp)}</span>}
              </div>
              <div className="mt-2 flex gap-2">
                <Button data-testid={WISHLIST.addToCartBtn(p.id)} size="sm" onClick={() => addAndRemove(p)} className="bg-hyd-red hover:bg-hyd-redDim text-white">
                  <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Move to cart
                </Button>
                <Button data-testid={WISHLIST.removeBtn(p.id)} size="sm" variant="ghost" onClick={() => toggle(p.id)} className="text-hyd-red">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
